import { useCallback, useRef, useState } from "react";
import { MagiClient, extractStructured } from "../api/magi";
import type { AppConfig } from "../config";
import type { Phase, RunRequest, StructuredResult, TaskStatus } from "../types";

export type DeliberationState = {
  phase: Phase;
  statusMessage: string;
  taskId: string | null;
  result: StructuredResult | null;
  rawResult: unknown;
  error: string | null;
  request: RunRequest | null;
  startedAt: number | null;
  completedAt: number | null;
};

const INITIAL: DeliberationState = {
  phase: "idle",
  statusMessage: "Standby",
  taskId: null,
  result: null,
  rawResult: null,
  error: null,
  request: null,
  startedAt: null,
  completedAt: null,
};

type Advance = Partial<DeliberationState> & { phase?: Phase };

export function useDeliberation(config: AppConfig) {
  const [state, setState] = useState<DeliberationState>(INITIAL);
  const abortRef = useRef<AbortController | null>(null);

  const patch = useCallback((next: Advance) => {
    setState((s) => ({ ...s, ...next }));
  }, []);

  const run = useCallback(
    async (req: RunRequest) => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      setState({
        ...INITIAL,
        phase: "dispatch",
        statusMessage: "Dispatching to models…",
        request: req,
        startedAt: Date.now(),
      });

      const client = new MagiClient(config);

      try {
        const { task_id } = await client.submit(req);
        patch({ taskId: task_id, phase: "processing", statusMessage: "Models processing…" });

        let final: TaskStatus | null = null;
        const stream = client.stream(task_id, ctrl.signal);
        for await (const evt of stream) {
          if (evt.event === "started") {
            patch({ phase: "processing", statusMessage: "Deliberation started." });
          } else if (evt.event === "running") {
            const msg =
              (evt.data.message as string | undefined) ?? "Models processing…";
            patch({ phase: "processing", statusMessage: msg });
          } else if (evt.event === "completed") {
            final = { ...(evt.data as unknown as TaskStatus) };
            break;
          } else if (evt.event === "failed") {
            throw new Error(
              (evt.data.error as string | undefined) ?? "Deliberation failed",
            );
          }
        }

        // Some servers close the stream without a 'completed' frame if the task
        // had already finished — fall back to polling.
        if (!final) {
          final = await client.getTask(task_id);
        }

        const structured = extractStructured(final.result);
        if (!structured && final.status !== "completed") {
          throw new Error(final.error || "No result returned");
        }

        // Phase-out: briefly pass through responses → aggregating → rapporteur
        // for cinematic effect, then complete. These transitions are purely
        // visual; by the time we get here, the task is already done.
        patch({ phase: "responses", statusMessage: "Responses collected." });
        await delay(400);
        if (req.deliberative) {
          patch({ phase: "crosstalk", statusMessage: "Peer review in progress…" });
          await delay(900);
        }
        patch({ phase: "aggregating", statusMessage: "Aggregating…" });
        await delay(350);
        patch({ phase: "rapporteur", statusMessage: "Rapporteur synthesising…" });
        await delay(350);

        patch({
          phase: "complete",
          statusMessage: "Deliberation complete.",
          result: structured,
          rawResult: final.result ?? null,
          completedAt: Date.now(),
        });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        patch({ phase: "failed", statusMessage: "Failed", error: message });
      }
    },
    [config, patch],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    setState(INITIAL);
  }, []);

  return { state, run, cancel };
}

function delay(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}
